import React from 'react';
import { isServer } from './isServer';

// useLayoutEffect warns when rendered on the server. The measurement only runs in the browser
// anyway, so fall back to useEffect there (neither runs during SSR).
const useIsomorphicLayoutEffect = isServer() ? React.useEffect : React.useLayoutEffect;

interface UseFittingItemCountOptions {
	/** Gap between the flex items, in px. Must match the container's CSS `gap`. */
	gap: number;
	/**
	 * Any change to this string re-runs the measurement. Include everything that can change an
	 * item's width (labels, item ids, badge values, ...), since the hook cannot observe that
	 * itself.
	 */
	measureKey: string;
	/**
	 * Class applied to the container while the natural widths are read. It must reveal the
	 * hidden items and neutralise anything that stretches an item (e.g. `flex-grow`). Added and
	 * removed within a single layout effect, so it is never painted.
	 */
	measuringClassName: string;
}

export interface FittingItemCount {
	/**
	 * How many `[data-fit-item]` elements fit. `null` until the first measurement, which means
	 * "render all of them" — that keeps the server and the first client render identical.
	 */
	visibleCount: number | null;
	/** Whether at least one item did not fit, i.e. whether `[data-fit-optional]` is needed. */
	isOverflowing: boolean;
}

/**
 * Measures how many `[data-fit-item]` elements fit on a single line inside `containerRef`,
 * beside the always-rendered `[data-fit-required]` element. If they do not all fit, space is
 * reserved for the `[data-fit-optional]` element and `isOverflowing` is reported as true.
 *
 * The container must be a flex container with `flex-wrap: nowrap` whose own width does not
 * depend on its children, otherwise the ResizeObserver can loop.
 */
export const useFittingItemCount = (
	containerRef: React.RefObject<HTMLElement>,
	{ gap, measureKey, measuringClassName }: UseFittingItemCountOptions,
): FittingItemCount => {
	const [result, setResult] = React.useState<FittingItemCount>({
		visibleCount: null,
		isOverflowing: false,
	});

	useIsomorphicLayoutEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		const widthOf = (element: Element | null) => (element ? Math.ceil(element.getBoundingClientRect().width) : 0);

		const apply = (visibleCount: number, isOverflowing: boolean) => {
			setResult((current) =>
				current.visibleCount === visibleCount && current.isOverflowing === isOverflowing
					? current
					: { visibleCount, isOverflowing },
			);
		};

		const measure = () => {
			// Reveal every item and stop the required item from growing, so we read natural widths.
			container.classList.add(measuringClassName);

			const available = Math.floor(container.clientWidth);
			const requiredWidth = widthOf(container.querySelector('[data-fit-required]'));
			const optionalWidth = widthOf(container.querySelector('[data-fit-optional]'));
			const itemWidths = Array.from(container.querySelectorAll('[data-fit-item]')).map(widthOf);

			container.classList.remove(measuringClassName);

			// Do all the items fit if we leave the optional element out entirely?
			const totalWithoutOptional = itemWidths.reduce((total, width) => total + gap + width, requiredWidth);
			if (totalWithoutOptional <= available) {
				apply(itemWidths.length, false);
				return;
			}

			// They do not, so reserve the optional element and take as many items as still fit.
			let used = requiredWidth + gap + optionalWidth;
			let count = 0;
			for (const width of itemWidths) {
				if (used + gap + width > available) break;
				used += gap + width;
				count++;
			}

			apply(count, true);
		};

		measure();

		const observer = new ResizeObserver(measure);
		observer.observe(container);

		// Web fonts are loaded with `display=swap`, so text metrics change after the swap lands.
		let isCancelled = false;
		void document.fonts.ready.then(() => {
			if (!isCancelled) measure();
		});

		return () => {
			isCancelled = true;
			observer.disconnect();
			container.classList.remove(measuringClassName);
		};
		// measureKey is not read in the effect — it is the caller's signal that the items, and
		// therefore their widths, have changed and need re-measuring.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerRef, gap, measureKey, measuringClassName]);

	return result;
};
